/**
 * Production Domain Check Utility
 * 
 * Ensures analytics tracking only occurs on production domains,
 * excluding Lovable editor/preview environments.
 */

// Production domains - only track traffic from these domains
const PRODUCTION_DOMAINS = [
  'xpandagent.io',
  'www.xpandagent.io',
  'xpansion-core.lovable.app', // Published app URL
];

// Domains to exclude (Lovable editor/preview)
const EXCLUDED_PATTERNS = [
  'lovable.dev',
  'lovableproject.com',
  'id-preview--',
  'localhost',
  '127.0.0.1',
];

/**
 * Check if the current domain is a production domain that should be tracked.
 * Returns false for Lovable editor, preview environments, and localhost.
 */
export function isProductionDomain(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  
  // Log for debugging (will be visible in browser console on production)
  const isProduction = checkIsProductionDomain(hostname);
  
  if (import.meta.env.DEV) {
    console.log('[Analytics] Domain check:', { hostname, isProduction });
  }
  
  return isProduction;
}

/**
 * Pure function to check if hostname is a production domain
 */
function checkIsProductionDomain(hostname: string): boolean {
  // Check if it's an excluded pattern first
  for (const pattern of EXCLUDED_PATTERNS) {
    if (hostname.includes(pattern)) {
      // Exception: allow the published lovable.app URL
      if (hostname === 'xpansion-core.lovable.app') {
        return true;
      }
      return false;
    }
  }
  
  // Check preview URLs that contain project IDs
  if (hostname.includes('.lovable.app') && hostname !== 'xpansion-core.lovable.app') {
    return false;
  }
  
  // Check if it's explicitly a production domain
  for (const prod of PRODUCTION_DOMAINS) {
    if (hostname === prod || hostname.endsWith(`.${prod}`)) {
      return true;
    }
  }
  
  // For any other domain (like xpandagent.io), allow tracking
  return true;
}

/**
 * Get the current hostname for logging/debugging purposes
 */
export function getCurrentHostname(): string {
  if (typeof window === 'undefined') return 'server';
  return window.location.hostname;
}
